import React, {useCallback, useEffect, useState} from "react";
import {Badge, Box, Button, CircularProgress, Divider, IconButton, Popover, Stack, Switch, Tooltip, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Settings02Icon, UserGroup03Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useWebrtc, WEBRTC_ROLES} from "../../context/WebrtcContext";
import {ProjectRoles} from "../../context/AppContext";
import {groupCount as countOf, WebrtcRepository, webrtcErrorCode, webrtcErrorMessage} from "../../repositories/WebrtcRepository";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";

const POLL_MS = 4000;

// Header button with the project's agent groups and a Join / Leave switch per group. A joined agent's browser rings
// for the group's calls while it is connected; membership stays across disconnects (offline agents are skipped).
// Project ADMIN / MANAGER / AGENT only.
export const AgentGroupsMenu = ({role}) => {
  const {project, basePath} = useRegistryProject();
  const navigate = useNavigate();
  // who creates / edits groups (Settings → Agent Groups)
  const canManage = [ProjectRoles.ADMIN, ProjectRoles.MANAGER].includes(role);
  const {status} = useWebrtc();
  const [anchor, setAnchor] = useState(null);
  const [groups, setGroups] = useState(null);
  const [pending, setPending] = useState(null);
  const allowed = WEBRTC_ROLES.includes(role);
  const connected = status === "registered";
  const projectId = project?.id;

  const load = useCallback(() => WebrtcRepository.getMyGroups({projectId})
    .then(({data}) => setGroups(data?.groups || []))
    .catch(e => console.error("Failed to load the agent groups", e)), [projectId]);

  // once for the badge, then polled while the list is open
  useEffect(() => {
    if (allowed && projectId) load();
  }, [allowed, projectId, load]);
  useEffect(() => {
    if (!anchor) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [anchor, load]);

  if (!allowed || !projectId) return null;

  const joinedCount = (groups || []).filter(group => group.joined).length;
  const waitingCount = (groups || []).filter(group => group.joined)
    .reduce((sum, group) => sum + (Number(group.waiting_count) || 0), 0);

  const toggle = async (group) => {
    const join = !group.joined;
    setPending(group.id);
    try {
      if (join) await WebrtcRepository.joinGroup({projectId, groupId: group.id});
      else await WebrtcRepository.leaveGroup({projectId, groupId: group.id});
      setGroups(current => current.map(item => item.id === group.id ? {...item, joined: join} : item));
      load();
    } catch (e) {
      toast.error(webrtcErrorCode(e) === "agent_not_registered"
        ? "Connect your browser phone once before joining a group"
        : webrtcErrorMessage(e, `Failed to ${join ? "join" : "leave"} "${group.name}"`));
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <Tooltip title={"Agent groups"}>
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label={"Agent groups"} sx={{
          width: 32, height: 32, borderRadius: "9px", border: "1px solid #E4E4EE", bgcolor: "#fff", color: "#23264D",
          "&:hover": {bgcolor: "#F4F4FA"},
        }}>
          <Badge
            badgeContent={waitingCount || joinedCount} invisible={!joinedCount}
            color={waitingCount ? "warning" : "primary"}
            sx={{"& .MuiBadge-badge": {fontSize: 10, height: 16, minWidth: 16, px: 0.5}}}
          >
            <HugeiconsIcon icon={UserGroup03Icon} size={16}/>
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {
          borderRadius: "14px", mt: 0.75, width: 340, maxWidth: "calc(100vw - 32px)",
          boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)",
        }}}
      >
        <Box sx={{px: 2, pt: 1.5, pb: 1}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>Agent groups</Typography>
          <Typography sx={{fontSize: 12, color: connected ? "#8A8AA0" : "#C7781E"}}>
            {connected
              ? "Calls to the groups you joined ring in this browser."
              : "Your browser phone is not connected: joined groups skip you until you connect."}
          </Typography>
        </Box>
        <Stack sx={{px: 1, pb: 1, maxHeight: 360, overflowY: "auto"}}>
          {groups === null && <Box sx={{display: "flex", justifyContent: "center", py: 2}}><CircularProgress size={18}/></Box>}
          {groups?.length === 0 && (
            <Typography sx={{fontSize: 12, color: "#8A8AA0", px: 1, py: 1.5}}>
              {canManage ? "No agent groups in this project yet. Create one below." : "No agent groups in this project yet. An admin adds them in Settings → Agent Groups."}
            </Typography>
          )}
          {groups?.map(group => (
            <Stack
              key={group.id} direction={"row"} alignItems={"center"} gap={1}
              sx={{px: 1, py: 0.75, borderRadius: "10px", opacity: group.joined && !connected ? 0.6 : 1, "&:hover": {bgcolor: "#FAFAFD"}}}
            >
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography noWrap sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>
                  {group.name}
                  <Box component={"span"} sx={{fontWeight: 500, color: "#8A8AA0", fontFamily: "monospace", ml: 0.75}}>
                    {group.number}
                  </Box>
                </Typography>
                <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                  <Box component={"span"} sx={{color: Number(group.waiting_count) ? "#C7781E" : "inherit", fontWeight: Number(group.waiting_count) ? 700 : 400}}>
                    {countOf(group.waiting_count)} waiting
                  </Box>
                  {" · "}{countOf(group.online_count)} online
                </Typography>
              </Box>
              {pending === group.id
                ? <Box sx={{width: 58, display: "flex", justifyContent: "center"}}><CircularProgress size={16}/></Box>
                : (
                  <Tooltip title={group.joined ? "Leave" : "Join"}>
                    <Switch
                      size={"small"} checked={Boolean(group.joined)} onChange={() => toggle(group)}
                      inputProps={{"aria-label": `${group.joined ? "Leave" : "Join"} ${group.name}`}}
                    />
                  </Tooltip>
                )}
            </Stack>
          ))}
        </Stack>
        {canManage && (
          <>
            <Divider/>
            <Box sx={{p: 1}}>
              <Button
                fullWidth startIcon={<HugeiconsIcon icon={Settings02Icon} size={16}/>}
                onClick={() => {
                  setAnchor(null);
                  navigate(`${basePath}/settings?tab=agent-groups`);
                }}
                sx={{justifyContent: "flex-start", textTransform: "none", fontSize: 13, fontWeight: 600, color: "#4B55C9", borderRadius: "10px"}}
              >
                Create and manage groups
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};
